'use client';

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProductReviews } from '@eliadmin/core/src/api/products/product-api';
import { reviewStats } from '@eliadmin/core/src/components/dlz/product-detail/reviews/reviews';
import { AddReviewDialog } from '@eliadmin/core/src/components/product/dialog/add-review-dialog';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { normalizeIntlLocale } from '@eliadmin/core/src/utils/intl-utils';

/**
 * Reviews of the open product. Same query key as the buy box rating, so the teaser, the tab and the
 * count in the buy box share one request. The distribution, filters and sorting are computed from
 * the list – the API returns neither facets nor pagination.
 */
export function useProductReviews() {
  const slug = useVariantsStore(state => state.product.slug);
  const locale = useLocaleStore(state => state.currentLocale);
  const enabled = !!useGlobalStore(
    state => state.templateSettings.product_detail?.reviews?.enabled,
  );

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [CacheKey.PRODUCTS_REVIEWS, locale, slug],
    queryFn: () => fetchProductReviews({ locale, slug }),
    select: response => response.data,
    enabled,
  });

  const stats = useMemo(() => (data ? reviewStats(data) : null), [data]);

  return { enabled, reviews: data, stats, isLoading, isError, refetch };
}

/** Number and date formats of the current locale. */
export function useReviewFormat() {
  const intlLocale = normalizeIntlLocale(useLocaleStore(state => state.currentLocale));
  return useMemo(() => {
    const average = new Intl.NumberFormat(intlLocale, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
    const date = new Intl.DateTimeFormat(intlLocale);
    return {
      average: (value: number) => average.format(value),
      date: (iso: string) => {
        const parsed = new Date(iso);
        return Number.isNaN(parsed.getTime()) ? '' : date.format(parsed);
      },
    };
  }, [intlLocale]);
}

/**
 * Entry points to writing a review. Only signed-in customers can post (the API takes an auth token),
 * `unknown` is the short time before the customer state is initialised on the client.
 */
export function useWriteReview() {
  const slug = useVariantsStore(state => state.product.slug);
  const openDialog = useDialogStore(state => state.open);
  const me = useCustomerStore(state => state.me);
  const isInitialized = useCustomerStore(state => state.isInitialized);

  return {
    status: !isInitialized
      ? ('unknown' as const)
      : me
        ? ('signed-in' as const)
        : ('signed-out' as const),
    write: () => openDialog({ id: AddReviewDialog.id, content: <AddReviewDialog slug={slug} /> }),
  };
}
